import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import type {
  ManualFinalizeBlockingIssue,
  ManualFinalizeWarningIssue,
} from '@/lib/api/mealPlans';
import {
  groupAffectedItemsByLocation,
  groupClinicalFinalizeIssues,
  type ClinicalWarningGroup,
} from '@/utils/clinical-finalize-warnings';

type ClinicalIssue = ManualFinalizeBlockingIssue | ManualFinalizeWarningIssue;

type PanelTone = 'blocked' | 'warning' | 'success' | 'checking';

type ClinicalFinalizeIssuesPanelProps = {
  tone: PanelTone;
  title: string;
  selectedDayNumber: number;
  visibleBlockingIssues: ManualFinalizeBlockingIssue[];
  visibleWarnings: ManualFinalizeWarningIssue[];
  otherDayBlockingIssues: ManualFinalizeBlockingIssue[];
  otherDayWarnings: ManualFinalizeWarningIssue[];
  otherDayNumbers: number[];
};

const PREVIEW_COUNT = 4;

const TONE_STYLES: Record<Exclude<PanelTone, 'checking'>, {
  bg: string;
  border: string;
  title: string;
  body: string;
  muted: string;
  divider: string;
  accent: string;
  badgeBg: string;
  badgeText: string;
  icon: string;
  buttonHover: string;
  rowBg: string;
}> = {
  blocked: {
    bg: '#FFF5F5',
    border: '#FECDCA',
    title: '#7A271A',
    body: '#912018',
    muted: '#B42318',
    divider: '#FECDCA',
    accent: '#F04438',
    badgeBg: '#FEE4E2',
    badgeText: '#B42318',
    icon: '#D92D20',
    buttonHover: 'rgba(217, 45, 32, 0.06)',
    rowBg: 'rgba(255,255,255,0.55)',
  },
  warning: {
    bg: '#FFFAF5',
    border: '#F9DBAF',
    title: '#7A2E0E',
    body: '#9A3412',
    muted: '#B54708',
    divider: '#F9DBAF',
    accent: '#F79009',
    badgeBg: '#FEF0C7',
    badgeText: '#B54708',
    icon: '#DC6803',
    buttonHover: 'rgba(247, 144, 9, 0.08)',
    rowBg: 'rgba(255,255,255,0.6)',
  },
  success: {
    bg: '#F6FEF9',
    border: '#A6F4C5',
    title: '#054F31',
    body: '#027A48',
    muted: '#039855',
    divider: '#A6F4C5',
    accent: '#12B76A',
    badgeBg: '#D1FADF',
    badgeText: '#027A48',
    icon: '#039855',
    buttonHover: 'rgba(18, 183, 106, 0.08)',
    rowBg: 'rgba(255,255,255,0.55)',
  },
};


const IssueGroupCard = ({
  group,
  styles,
}: {
  group: ClinicalWarningGroup;
  styles: (typeof TONE_STYLES)['warning'];
}) => (
  <Box
    sx={{
      display: 'flex',
      gap: 1,
      alignItems: 'stretch',
      bgcolor: styles.rowBg,
      borderRadius: '8px',
      px: 1.1,
      py: 0.9,
    }}
  >
    <Box
      sx={{
        width: 2.5,
        flexShrink: 0,
        borderRadius: '999px',
        bgcolor: styles.accent,
        alignSelf: 'stretch',
        minHeight: 28,
      }}
    />
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 0.55,
          mb: 0.55,
        }}
      >
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 700,
            color: styles.title,
            lineHeight: 1.3,
          }}
        >
          {group.title}
        </Typography>
      </Box>
      <Stack spacing={0.75}>
        {group.warnings.map((warning) => {
          const affectedLocations = groupAffectedItemsByLocation(warning);
          return (
            <Box key={warning.key}>
              <Typography sx={{ fontSize: 10, color: styles.body }}>Rule: {warning.ruleId}</Typography>
              {affectedLocations.length > 0 ? (
                <>
                  <Typography
                    sx={{
                      fontSize: '11px',
                      fontWeight: 600,
                      color: styles.body,
                      lineHeight: 1.45,
                    }}
                  >
                    {warning.message}
                  </Typography>
                  <Stack spacing={0.25} sx={{ mt: 0.35 }}>
                    {affectedLocations.map(({ location, itemNames }) => (
                      <Typography
                        key={location}
                        sx={{
                          fontSize: '11px',
                          fontWeight: 400,
                          color: styles.body,
                          lineHeight: 1.45,
                          opacity: 0.9,
                        }}
                      >
                        <Box component="span" sx={{ fontSize: '10px', fontWeight: 700, color: styles.badgeText }}>
                          {location}:
                        </Box>{' '}
                        {itemNames.join(', ')}
                      </Typography>
                    ))}
                  </Stack>
                </>
              ) : (
                <>
                  <Typography
                    sx={{
                      fontSize: '10px',
                      fontWeight: 700,
                      color: styles.badgeText,
                      lineHeight: 1.4,
                    }}
                  >
                    {warning.locations.join(' • ')}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.15,
                      fontSize: '11px',
                      fontWeight: 400,
                      color: styles.body,
                      lineHeight: 1.45,
                      opacity: 0.9,
                    }}
                  >
                    {warning.message}
                  </Typography>
                </>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  </Box>
);

const ExpandLink = ({
  styles,
  expanded,
  onClick,
  children,
}: {
  styles: (typeof TONE_STYLES)['warning'];
  expanded: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <Button
    size="small"
    color="inherit"
    aria-expanded={expanded}
    onClick={onClick}
    endIcon={(
      <ExpandMoreRoundedIcon
        sx={{
          fontSize: 15,
          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 160ms ease',
        }}
      />
    )}
    sx={{
      px: 0.25,
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '11px',
      color: styles.title,
      minHeight: 0,
      '&:hover': { bgcolor: styles.buttonHover },
    }}
  >
    {children}
  </Button>
);

export default function ClinicalFinalizeIssuesPanel({
  tone,
  title,
  selectedDayNumber,
  visibleBlockingIssues,
  visibleWarnings,
  otherDayBlockingIssues,
  otherDayWarnings,
}: ClinicalFinalizeIssuesPanelProps) {
  const [showAllVisible, setShowAllVisible] = React.useState(false);

  const allIssues = React.useMemo<ClinicalIssue[]>(
    () => [
      ...visibleBlockingIssues,
      ...visibleWarnings,
      ...otherDayBlockingIssues,
      ...otherDayWarnings,
    ],
    [otherDayBlockingIssues, otherDayWarnings, visibleBlockingIssues, visibleWarnings],
  );
  const issueGroups = React.useMemo(
    () => groupClinicalFinalizeIssues(allIssues),
    [allIssues],
  );

  React.useEffect(() => {
    setShowAllVisible(false);
  }, [selectedDayNumber, allIssues.length]);

  const styles = TONE_STYLES[tone === 'checking' ? 'warning' : tone];
  const subtitle = tone === 'blocked'
    ? 'Resolve these grouped plan-wide issues before finalising. Related items are combined by meal.'
    : tone === 'warning'
      ? 'Related items are combined by day and meal.'
      : tone === 'checking'
        ? 'Running clinical safety checks…'
        : 'All clinical hard-block checks passed.';

  const previewGroups = showAllVisible ? issueGroups : issueGroups.slice(0, PREVIEW_COUNT);
  const hiddenVisibleCount = Math.max(0, issueGroups.length - PREVIEW_COUNT);

  const HeaderIcon = tone === 'success'
    ? CheckCircleOutlineRoundedIcon
    : tone === 'blocked'
      ? ErrorOutlineRoundedIcon
      : WarningAmberRoundedIcon;

  return (
    <Box
      role="alert"
      sx={{
        mb: 1.5,
        borderRadius: '10px',
        border: '1px solid',
        borderColor: styles.border,
        bgcolor: styles.bg,
        px: 1.5,
        py: 1.25,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.9 }}>
        <HeaderIcon
          sx={{
            mt: 0.05,
            fontSize: 17,
            color: styles.icon,
            flexShrink: 0,
          }}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontSize: '12.5px',
              fontWeight: 700,
              color: styles.title,
              lineHeight: 1.3,
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              mt: 0.2,
              fontSize: '11px',
              color: styles.muted,
              lineHeight: 1.35,
              opacity: 0.92,
            }}
          >
            {subtitle}
          </Typography>
        </Box>
      </Box>

      {issueGroups.length > 0 && tone !== 'checking' ? (
        <>
          <Box
            sx={{
              mt: 1.1,
              mb: 1.1,
              borderTop: '1px solid',
              borderColor: styles.divider,
            }}
          />

          <Stack spacing={0.85}>
            {previewGroups.map((group) => (
              <IssueGroupCard key={group.key} group={group} styles={styles} />
            ))}
          </Stack>

          {hiddenVisibleCount > 0 ? (
            <Box sx={{ mt: 0.85 }}>
              <ExpandLink
                styles={styles}
                expanded={showAllVisible}
                onClick={() => setShowAllVisible((value) => !value)}
              >
                {showAllVisible
                  ? 'Show fewer'
                  : `Show ${hiddenVisibleCount} more clinical group${hiddenVisibleCount === 1 ? '' : 's'}`}
              </ExpandLink>
            </Box>
          ) : null}
        </>
      ) : null}
    </Box>
  );
}
